import { useState, useRef, useCallback, useEffect } from 'react'
import type { PickedPhoto } from 'components/PhotoBank/PhotoBank.types'
import type {
  GooglePhotosSession,
  GooglePhotosMediaItem,
  GooglePhotosAuthConfig,
  GooglePhotosPickingOptions,
  UseGooglePhotosReturn,
} from './GooglePhotosPicker.types'
import { GooglePhotosStatus } from './GooglePhotosPicker.types'
import { logger } from './logger'

declare global {
  interface Window {
    google: any
  }
}

// As per https://developers.google.com/photos/overview/authorization
const SCOPES = [
  'https://www.googleapis.com/auth/photospicker.mediaitems.readonly',
]

let pickerWindow: Window | null = null

// Implements the Google Photos Picker flow from here:
// https://developers.google.com/photos/picker/guides/get-started-picker
export function useGooglePhotos(): UseGooglePhotosReturn {
  const [accessToken, setAccessToken] = useState<string>('')
  const [status, setStatus] = useState<GooglePhotosStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pendingPickingOptions, setPendingPickingOptions] =
    useState<GooglePhotosPickingOptions | null>(null)
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const currentPromiseRef = useRef<{
    resolve: (photos: PickedPhoto[]) => void
    reject: (error: Error) => void
  } | null>(null)
  const completionCallbackRef = useRef<
    ((photos: PickedPhoto[]) => void) | null
  >(null)

  const cleanup = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }
  }, [])

  const reset = useCallback(() => {
    setStatus(null)
    setError(null)
    setPendingPickingOptions(null)
    completionCallbackRef.current = null
    cleanup()
    if (currentPromiseRef.current) {
      currentPromiseRef.current.reject(new Error('Operation reset'))
      currentPromiseRef.current = null
    }
  }, [cleanup])

  useEffect(() => {
    return cleanup
  }, [cleanup])

  const authenticate = useCallback(
    (
      config: GooglePhotosAuthConfig,
      onSuccess: (accessToken: string) => void,
      onError: () => void
    ): void => {
      const originalOpen = window.open
      window.open = function (url, name, features) {
        if (url && url.toString().includes('accounts.google.com')) {
          const width = 500
          const height = 600
          const left = window.screen.width / 2 - width / 2
          const top = window.screen.height / 2 - height / 2
          const centeredFeatures = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
          return originalOpen.call(this, url, name, centeredFeatures)
        }
        return originalOpen.call(this, url, name, features)
      }

      window.google.accounts.oauth2
        .initTokenClient({
          client_id: config.clientId,
          scope: SCOPES.join(' '),
          prompt: 'consent',
          callback: (response: any) => {
            window.open = originalOpen

            if (response.access_token) {
              setAccessToken(response.access_token)
              onSuccess(response.access_token)
            } else {
              onError()
            }
          },
        })
        .requestAccessToken()
    },
    []
  )

  // https://developers.google.com/photos/picker/guides/sessions#create-sessions
  const createPickerSession = useCallback(
    async (
      token: string,
      options: GooglePhotosPickingOptions
    ): Promise<GooglePhotosSession> => {
      const requestBody: any = {}

      if (options.singleFileOnly) {
        requestBody.pickingConfig = {
          maxItemCount: '1',
        }
      }

      const response = await fetch(
        'https://photospicker.googleapis.com/v1/sessions',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }
      )

      if (!response.ok) {
        const errorText = await response.text()
        logger.error('Google Photos: Session creation error', null, {
          message: errorText,
        })
        throw new Error(
          `Google Photos: Failed to create picker session: ${response.status} ${response.statusText}`
        )
      }

      const session = await response.json()

      if (!session.pickerUri || !session.id) {
        throw new Error('Google Photos: Invalid session response')
      }

      return session
    },
    []
  )

  // https://developers.google.com/photos/picker/guides/sessions#poll-sessions
  const pollSessionStatus = useCallback(
    async (token: string, sessionId: string): Promise<GooglePhotosSession> => {
      const response = await fetch(
        `https://photospicker.googleapis.com/v1/sessions/${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (!response.ok) {
        throw new Error('Google Photos: Failed to poll session status')
      }

      return await response.json()
    },
    []
  )

  // https://developers.google.com/photos/picker/guides/media-items#list_picked_media_items
  const fetchSelectedMediaItems = useCallback(
    async (
      token: string,
      sessionId: string
    ): Promise<GooglePhotosMediaItem[]> => {
      const response = await fetch(
        `https://photospicker.googleapis.com/v1/mediaItems?sessionId=${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (!response.ok) {
        throw new Error('Google Photos: Failed to fetch selected items')
      }

      const data = await response.json()
      return data.mediaItems || []
    },
    []
  )

  // https://developers.google.com/photos/picker/guides/media-items
  const downloadMediaItemsAsFiles = useCallback(
    async (
      token: string,
      mediaItems: GooglePhotosMediaItem[]
    ): Promise<PickedPhoto[]> => {
      const files: File[] = []

      for (const item of mediaItems) {
        try {
          const response = await fetch(`${item.mediaFile.baseUrl}=d`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })

          if (!response.ok) {
            logger.error('Google Photos: Failed to download media item', null, {
              message: response.statusText,
              mediaItem: item.mediaFile.filename,
              url: item.mediaFile.baseUrl,
            })
            continue
          }

          const blob = await response.blob()
          const file = new File([blob], item.mediaFile.filename, {
            type: blob.type || 'image/jpeg',
          })
          files.push(file)
        } catch (error) {
          logger.error('Google Photos: Error downloading media item', error, {
            mediaItem: item.mediaFile.filename,
            url: item.mediaFile.baseUrl,
          })
        }
      }

      return files
    },
    []
  )

  const pollUntilComplete = useCallback(
    async (token: string, sessionId: string): Promise<void> => {
      try {
        const data = await pollSessionStatus(token, sessionId)

        if (data.mediaItemsSet) {
          setStatus(GooglePhotosStatus.FETCHING_IMAGES)
          const mediaItems = await fetchSelectedMediaItems(token, sessionId)
          const downloadedPhotos = await downloadMediaItemsAsFiles(
            token,
            mediaItems
          )

          setStatus(null)
          setError(null)
          pickerWindow?.close()

          // Call the persistent completion callback first
          if (completionCallbackRef.current) {
            completionCallbackRef.current(downloadedPhotos)
            completionCallbackRef.current = null
          }

          // Then resolve the promise if it exists
          if (currentPromiseRef.current) {
            currentPromiseRef.current.resolve(downloadedPhotos)
            currentPromiseRef.current = null
          }
        } else {
          const delay =
            (data.pollingConfig?.nextPollInSeconds ||
              data.pollingPolicy?.nextPollInSeconds ||
              3) * 1000
          pollTimeoutRef.current = setTimeout(() => {
            pollUntilComplete(token, sessionId)
          }, delay)
        }
      } catch (error) {
        setStatus(null)
        const errorMessage = 'Google Photos: Error polling session status'
        setError(errorMessage)
        logger.error(errorMessage, error)

        if (currentPromiseRef.current) {
          currentPromiseRef.current.reject(error as Error)
          currentPromiseRef.current = null
        }
      }
    },
    [pollSessionStatus, fetchSelectedMediaItems, downloadMediaItemsAsFiles]
  )

  const handlePickingFlow = useCallback(
    async (
      token: string,
      options: GooglePhotosPickingOptions
    ): Promise<void> => {
      try {
        // Open the window immediately to prevent popup blockers
        pickerWindow = window.open(
          'about:blank',
          'photoPicker',
          'fullscreen=yes,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes'
        )

        if (!pickerWindow) {
          throw new Error('Popup blocked or failed to open')
        }

        setStatus(GooglePhotosStatus.CREATING_SESSION)
        const session = await createPickerSession(token, options)

        // Navigate the already-open window to the picker URI
        pickerWindow.location.href = session.pickerUri
        setStatus(GooglePhotosStatus.CHOOSING_IMAGES)

        await pollUntilComplete(token, session.id)
      } catch (error) {
        // Close the window if something went wrong
        if (pickerWindow) {
          pickerWindow.close()
          pickerWindow = null
        }

        setStatus(null)
        const errorMessage = 'Google Photos: Error picking photos'
        setError(errorMessage)
        logger.error(errorMessage, error)

        if (currentPromiseRef.current) {
          currentPromiseRef.current.reject(error as Error)
          currentPromiseRef.current = null
        }
      }
    },
    [createPickerSession, pollUntilComplete]
  )

  const startPhotoPicking = useCallback(
    async (
      config: GooglePhotosAuthConfig,
      options: GooglePhotosPickingOptions = {},
      onAuthSuccess?: () => Promise<void>
    ): Promise<PickedPhoto[]> => {
      setError(null)

      return new Promise<PickedPhoto[]>((resolve, reject) => {
        currentPromiseRef.current = { resolve, reject }

        const executeFlow = async () => {
          try {
            if (accessToken) {
              setStatus(GooglePhotosStatus.AUTHENTICATED)
              setPendingPickingOptions(options)
              if (onAuthSuccess) {
                await onAuthSuccess()
              }
            } else {
              setStatus(GooglePhotosStatus.AUTHENTICATING)
              await authenticate(
                config,
                async (_token) => {
                  setStatus(GooglePhotosStatus.AUTHENTICATED)
                  setPendingPickingOptions(options)
                  if (onAuthSuccess) {
                    await onAuthSuccess()
                  }
                },
                () => {
                  setStatus(null)
                  const errorMessage = 'Google Photos: Authentication failed'
                  setError(errorMessage)
                  logger.error(errorMessage, null, {
                    message: errorMessage,
                  })
                  if (currentPromiseRef.current) {
                    currentPromiseRef.current.reject(
                      new Error('Authentication failed')
                    )
                    currentPromiseRef.current = null
                  }
                }
              )
            }
          } catch (error) {
            setStatus(null)
            const errorMessage = 'Google Photos: Error in picking photos flow'
            setError(errorMessage)
            logger.error(errorMessage, error)
            setPendingPickingOptions(null)
            if (currentPromiseRef.current) {
              currentPromiseRef.current.reject(error as Error)
              currentPromiseRef.current = null
            }
          }
        }

        executeFlow()
      })
    },
    [accessToken, authenticate]
  )

  const proceedWithPendingPicking = useCallback(async () => {
    if (!accessToken || !pendingPickingOptions) {
      return
    }

    setPendingPickingOptions(null)
    await handlePickingFlow(accessToken, pendingPickingOptions)
  }, [accessToken, pendingPickingOptions, handlePickingFlow])

  const setCompletionCallback = useCallback(
    (callback: (photos: PickedPhoto[]) => void) => {
      completionCallbackRef.current = callback
    },
    []
  )

  return {
    startPhotoPicking,
    proceedWithPendingPicking,
    setCompletionCallback,
    status,
    error,
    hasToken: !!accessToken,
    hasPendingPicking: !!pendingPickingOptions,
    reset,
  }
}
